import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { http, gradeLabel } from "@/lib/api";
import { ReflectionCard } from "@/components/teacher/ReflectionCard";
import { CommentEditor } from "@/components/teacher/CommentEditor";
import { RecordLinks } from "@/components/records/RecordShell";
import { AssessmentSummary } from "@/components/assessment/AssessmentSummary";
import { GradeEditor } from "@/components/parent/FamilyParts";
import { BuddyInsights } from "@/components/teacher/BuddyInsights";
import { BuddyGrowthReport } from "@/components/teacher/BuddyGrowthReport";
import { PathwayProgress } from "@/components/teacher/PathwayProgress";
import { LiveConsent } from "@/components/teacher/LiveConsent";
import { SpanishDefault } from "@/components/SpanishDefault";

const STATS = [
  ["lessons_completed", "Lessons"], ["accuracy", "Quiz accuracy", "%"], ["mindful_minutes", "Mindful min"],
  ["streak_days", "Day streak"], ["mood_lift", "Avg mood lift"],
];

export default function StudentDetail() {
  const { classId, studentId } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { http.get(`/classes/${classId}/students/${studentId}`).then((r) => setD(r.data)).catch(() => setErr("Student not found in this class.")); }, [classId, studentId]);
  if (err) return <div className="max-w-6xl mx-auto px-6 py-20 text-stone-600" data-testid="student-detail-error">{err} <Link to="/teacher" className="underline text-[#C87D53]">Back to classes</Link></div>;
  if (!d) return <div className="max-w-6xl mx-auto px-6 py-20 text-stone-500">Loading…</div>;

  const goal = d.class.min_minutes;
  const max = Math.max(goal, ...d.attendance.map((a) => a.minutes));
  const presentDays = d.attendance.filter((a) => a.minutes >= goal).length;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12" data-testid="student-detail-page">
      <Link to={`/teacher/classes/${classId}`} className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-[#2C4A3E]" data-testid="student-back-link"><ArrowLeft className="w-4 h-4" /> {d.class.name}</Link>
      <h1 className="font-display text-4xl sm:text-5xl text-[#2C4A3E] mt-6" data-testid="student-name">{d.student.name}</h1>
      <p className="text-stone-500 mt-2">{gradeLabel(d.student.grade)} · {d.student.email}</p>
      <div className="mt-4 max-w-xl"><SpanishDefault sid={studentId} name={d.student.name.split(" ")[0]} /></div>
      <div className="mt-4 no-print"><GradeEditor studentId={studentId} grade={d.student.grade} testId="student-grade-editor" /></div>
      <RecordLinks studentId={studentId} className="mt-5 no-print" />
      <AssessmentSummary studentId={studentId} />
      <Link to={`/planner/${studentId}`} data-testid="student-planner-link" className="mt-3 inline-flex px-4 py-2 rounded-full bg-[#2C4A3E] text-white text-sm no-print">Assignments plan (day / week / month)</Link>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-10">
        {STATS.map(([k, label, suffix = ""]) => (
          <div key={k} className="rounded-2xl bg-white/60 border border-[#E6DEC3] p-4">
            <div className="font-display text-2xl text-[#2C4A3E]" data-testid={`student-stat-${k}`}>{d.stats[k]}{suffix}</div>
            <div className="text-xs uppercase tracking-widest text-stone-500 mt-1">{label}</div>
          </div>
        ))}
      </div>

      <section className="mt-10 rounded-3xl bg-white/60 border border-[#E6DEC3] p-6" data-testid="student-attendance">
        <div className="flex justify-between flex-wrap gap-2">
          <p className="text-xs uppercase tracking-widest text-stone-500">Seat-time · last 30 days</p>
          <p className="text-sm text-[#2C4A3E]"><b>{presentDays}</b> present days (≥ {goal} min)</p>
        </div>
        <div className="flex items-end gap-1 h-28 mt-5">
          {d.attendance.map((a) => (
            <div key={a.date} title={`${a.date}: ${a.minutes} min`} className={`flex-1 rounded-t ${a.minutes >= goal ? "bg-[#6B9080]" : a.minutes > 0 ? "bg-[#E8B4B8]" : "bg-[#F1EADB]"}`}
              style={{ height: `${Math.max(4, (a.minutes / max) * 100)}%` }} />
          ))}
        </div>
      </section>

      <BuddyInsights classId={classId} studentId={studentId} />
      <BuddyGrowthReport classId={classId} studentId={studentId} />
      <PathwayProgress classId={classId} studentId={studentId} />
      <LiveConsent classId={classId} studentId={studentId} />
      <CommentEditor classId={classId} studentId={studentId} entries={d.entries} />
      <h2 className="font-display text-2xl text-[#2C4A3E] mt-12">Lessons & reflections</h2>
      <div className="grid md:grid-cols-2 gap-5 mt-5" data-testid="student-entries">
        {d.entries.map((e) => <ReflectionCard key={e.id} e={e} />)}
      </div>
      {!d.entries.length && <p className="text-stone-500 py-8" data-testid="student-entries-empty">No lessons completed yet.</p>}
    </div>
  );
}
